import { useCallback } from 'react';

import { useBarData } from './useBarData.js';
import { useBuildStrategy } from './useBuildStrategy.js';
import { useRectData } from './useRectData.js';
import { useXYChartData } from './useXYChartData.js';
import { useXYChartScales } from './useXYChartScales.js';
import type { SeriesToHoverGrid } from '../context/XYChartOverlayPerformance.context.js';
import type {
  BarSeriesWithDatapoints,
  RectSeriesWithDatapoints,
} from '../types/xy-chart-internals.js';
import { buildHoverGrids } from '../utils/query-hovered-grid.js';

/**
 * Build per-series hover grids for rect and bar series on a debounced
 * schedule (via `useBuildStrategy`). Returns `undefined` while the
 * grid is being (re)built; callers should fall back to the linear
 * `buildQueryHovered` / `buildQueryAllHovered` path in that window.
 *
 * Both tooltip variants (single and shared) consume this grid via
 * `buildQueryHoveredFromGrid` and `buildQueryAllHoveredFromGrid`
 * respectively.
 */
export function useBuildHoverGrid(): SeriesToHoverGrid | undefined {
  const { filteredSeriesWithDatapoints: rectSeriesData } = useRectData();
  const { filteredSeriesWithDatapoints: barSeriesData } = useBarData();
  const axisIdToScales = useXYChartScales();
  const { datapointMetadataMap, axisIdToScaleDomain, seriesToMetadata } =
    useXYChartData();

  const build = useCallback(() => {
    const series: (RectSeriesWithDatapoints | BarSeriesWithDatapoints)[] = [
      ...rectSeriesData,
      ...barSeriesData,
    ];
    return buildHoverGrids(
      series,
      axisIdToScales,
      datapointMetadataMap,
      seriesToMetadata,
      axisIdToScaleDomain,
    );
  }, [
    rectSeriesData,
    barSeriesData,
    axisIdToScales,
    datapointMetadataMap,
    seriesToMetadata,
    axisIdToScaleDomain,
  ]);

  return useBuildStrategy<SeriesToHoverGrid>(build);
}
